import _clamp from 'lodash/clamp';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { addDays, startOfWeek, isSameDay, addWeeks, differenceInCalendarWeeks } from 'date-fns';
import { t } from '../../../../../i18n';
import { SELECTED_SIGNAL } from '../../../../signals';
import { HIDE_POPUP_SIGNAL } from '../../../../../popupContainer/popupContainer.constants';
import { fetchEPGLayout } from '../../../../../../logic/layout/layout.actions';
import { formatDate } from '../epg.utils';
import { EpgPopupHeader } from './epgPopupHeader/epgPopupHeader.component';
import { EpgPopupEntry } from './epgPopupEntry/epgPopupEntry.component';
import { defaultContext, getExpandedPosition, getInitialStyle, LIST_GAP } from './epgPopup.style';
import { sendEvent } from '../../../../../../logic/analytics/analytics.utils';

export class EpgPopup extends Lightning.Component {
  static _template() {
    return _merge(
      {
        rect: true,
        passSignals: {
          [HIDE_POPUP_SIGNAL]: true,
        },
        Container: {
          rect: true,
          Header: {
            type: EpgPopupHeader,
            title: t('epgGrid.currentWeek'),
            signals: {
              [SELECTED_SIGNAL]: '_handleWeekSelected',
            },
          },
        },
        List: {},
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Header extends this {
        _getFocused() {
          return this.tag('Header');
        }

        _handleDown() {
          this._setState('List');
        }
      },
      class List extends this {
        $enter() {
          this._index = 0;
        }

        _handleUp() {
          if (this._index > 0) {
            this._index -= 1;
          } else {
            this._setState('Header');
          }

          return true;
        }

        _handleDown() {
          if (this._index < this.tag('List').children.length - 1) {
            this._index += 1;
          }

          return true;
        }

        _getFocused() {
          return this.tag('List').children[this._index];
        }
      },
    ];
  }

  _construct() {
    this._style = defaultContext;
    this._offset = 0;
  }

  _active() {
    this.patch({
      Container: {
        smooth: {
          x: getExpandedPosition(this._style),
        },
      },
    });

    this._setState('List');
  }

  _detach() {
    this._date = undefined;
  }

  get blockId() {
    return this._blockId;
  }

  set blockId(value) {
    this._blockId = value;
  }

  get date() {
    return this._date;
  }

  set date(value) {
    this._date = value;
    this._updateDate();
  }

  get selectedDate() {
    return this._selectedDate;
  }

  set selectedDate(value) {
    this._selectedDate = value;
    sendEvent({ key: 'epgDateSelect', date: this._selectedDate });
    this._updateDate();
  }

  _handleBack() {
    this.signal(HIDE_POPUP_SIGNAL);

    return true;
  }

  _handleEntrySelected(date) {
    this.tag('List').children.forEach(child => {
      child.selected = isSameDay(child.date, date);
    });

    Store.dispatch(fetchEPGLayout({ date, blockId: this.blockId }));

    this.signal(HIDE_POPUP_SIGNAL);
  }

  _handleWeekSelected(offset) {
    this._offset = offset;
    this._update();
  }

  _updateDate() {
    if (!this._date || !this._selectedDate) {
      return;
    }

    this._offset = _clamp(differenceInCalendarWeeks(this._selectedDate, this._date, { weekStartsOn: 1 }), -1, 1);
    this.tag('Header').index = this._offset + 1;
    this._update();
  }

  _update() {
    const start = startOfWeek(addWeeks(this._date, this._offset), { weekStartsOn: 1 });
    const now = new Date();
    const days = [];

    for (let i = 0; i < 7; i += 1) {
      days.push(addDays(start, i));
    }

    this.tag('List').children = days.map(dayOfWeek => {
      const isToday = isSameDay(dayOfWeek, now);
      const isSelected = isSameDay(dayOfWeek, this._selectedDate);

      return {
        type: EpgPopupEntry,
        title: isToday ? t('epgGrid.today') : formatDate(dayOfWeek),
        selected: isSelected,
        date: dayOfWeek,
        signals: {
          [SELECTED_SIGNAL]: '_handleEntrySelected',
        },
        flexItem: {
          marginBottom: LIST_GAP,
        },
      };
    });
  }
}
